import { makeOptional } from "@/shared/lib/helpers/schema.helpers";
import { selectSchema, textSchema, dateSchema } from "@/shared/lib/schemas/common.schema";
import { z } from "zod";

export const volunteerQueryParamsSchema = z.object({
    page: z.number().min(1, "Page must be greater than 0"),
    limit: z.number().min(1, "Limit must be greater than 0"),
    search: makeOptional(textSchema("md")),
    organization: makeOptional(textSchema("md")),
    roles: makeOptional(z.union([z.array(z.string()), z.string()]).transform(val =>
        Array.isArray(val) ? val : typeof val === "string" ? val.split(",").map((s) => s.trim()).filter(Boolean) : []
    )),
    locationServing: makeOptional(textSchema("md")),
    arrivalDate: makeOptional(z.union([z.string(), z.date()]).transform(val => {
        if (!val) return undefined;
        if (val instanceof Date) {
            return val.toISOString().split('T')[0]; // Convert Date to YYYY-MM-DD
        }
        return val; // Already a string
    })),
    departureDate: makeOptional(z.union([z.string(), z.date()]).transform(val => {
        if (!val) return undefined;
        if (val instanceof Date) {
            return val.toISOString().split('T')[0];
        }
        return typeof val === 'string' ? val : undefined;
    })),
    addedFrom: makeOptional(z.string()),
    addedTo: makeOptional(z.string()),
    lastActiveAtFrom: makeOptional(z.string()),
    lastActiveAtTo: makeOptional(z.string()),
    status: makeOptional(z.union([z.boolean(), z.enum(['active', 'inactive', 'true', 'false'])]).transform(val => {
        if (val === undefined || val === null || (val as unknown) === '') return undefined;
        if (val === true || val === 'true' || val === 'active') return true;
        if (val === false || val === 'false' || val === 'inactive') return false;
        return undefined;
    })),
    /** Filter by serveDates.regionId: only users with at least one serve period for this region (for AR Team calendar) */
    serveDatesRegionId: makeOptional(z.coerce.number(), "number"),
    /** Client local-day bounds for activeTodayCount fallback (ISO strings) */
    activeTodayStart: makeOptional(z.string()),
    activeTodayEnd: makeOptional(z.string()),
    /** IANA timezone (e.g. America/New_York) for server-side count matching UI dates */
    clientTimeZone: makeOptional(z.string().max(100)),
}) 

export const volunteerQueryParamsWithoutPaginationSchema = volunteerQueryParamsSchema.omit({
    page: true,
    limit: true,
})

export const transformVolunteerQueryParamsSchema = volunteerQueryParamsSchema.transform((data) => {
    return {
        ...data,
        serveDatesRegionId: data.serveDatesRegionId && data.serveDatesRegionId > 0 ? Number(data.serveDatesRegionId) : undefined,
    }
})


export type VolunteerQueryParamsType = z.infer<typeof volunteerQueryParamsSchema>
export type VolunteerQueryParamsWithOutPaginationType = z.infer<typeof volunteerQueryParamsWithoutPaginationSchema>
export type TransformedVolunteerQueryParamsType = z.infer<typeof transformVolunteerQueryParamsSchema>